import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  createTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { ChangeCustomer, Customer } from "../../models/AdminCustomersModels";
import { changeCustomer } from "../../adminCustomersSlice";
import {
  CustomerSizeCode,
  CustomerTurnoverCode,
  customerSize,
  customerTurnover,
  enumToArray
} from "../../../shared/models/models";

export interface CustomerDialogProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
}

const CustomerDialog = ({ open, onClose, customer }: CustomerDialogProps) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ChangeCustomer>();

  const onSubmit: SubmitHandler<ChangeCustomer> = (data: ChangeCustomer) => {
    handlerChangeCustomer(data);
  };

  const handlerChangeCustomer = async (model: ChangeCustomer) => {
    if (customer) {
      model.turnoverCode = model.turnoverCode || undefined;
      model.sizeCode = model.sizeCode || undefined;
      await dispatch(changeCustomer({ id: customer.id, model }));
      onClose();
    }
  };

  const init = () => {
    reset(customer);
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px"
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              width: "600px",
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });
  return (
    <>
      <ThemeProvider theme={theme}>
        <Dialog open={open} onClose={onClose}>
          <DialogTitle>
            <p className="heading-2">Edit customer details</p>
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 14,
              top: 14,
              color: "#333333"
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent>
            {customer && (
              <form id="form" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <InputLabel>Name</InputLabel>
                  <TextField
                    className="w-full"
                    {...register("name", { required: "Field is required" })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  ></TextField>
                </div>

                <Box className="grid grid-cols-2 gap-5 mt-4">
                  <div>
                    <InputLabel>Tax ID</InputLabel>
                    <TextField
                      className="w-full"
                      {...register("taxId")}
                      error={!!errors.taxId}
                      helperText={errors.taxId?.message}
                    ></TextField>
                  </div>

                  <div>
                    <InputLabel>VAT ID</InputLabel>
                    <TextField
                      className="w-full"
                      {...register("vatId")}
                      error={!!errors.vatId}
                      helperText={errors.vatId?.message}
                    ></TextField>
                  </div>
                </Box>

                <div className="mt-4">
                  <InputLabel>Website</InputLabel>
                  <TextField
                    className="w-full"
                    {...register("website")}
                    error={!!errors.website}
                    helperText={errors.website?.message}
                  ></TextField>
                </div>

                <div className="mt-4">
                  <InputLabel>Turnover</InputLabel>
                  <FormControl className="w-full">
                    <Select className="w-full" defaultValue={customer.turnoverCode || ""} {...register("turnoverCode")}>
                      <MenuItem value="">Unknown</MenuItem>
                      {enumToArray<CustomerTurnoverCode>(CustomerTurnoverCode).map((code) => (
                        <MenuItem key={code} value={code}>
                          {customerTurnover(code)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error content={errors.turnoverCode?.message} />
                  </FormControl>
                </div>

                <div className="mt-4">
                  <InputLabel>Size</InputLabel>
                  <FormControl className="w-full">
                    <Select className="w-full" defaultValue={customer.sizeCode || ""} {...register("sizeCode")}>
                      <MenuItem value="">Unknown</MenuItem>
                      {enumToArray<CustomerSizeCode>(CustomerSizeCode).map((code) => (
                        <MenuItem key={code} value={code}>
                          {customerSize(code)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error content={errors.sizeCode?.message} />
                  </FormControl>
                </div>
              </form>
            )}
          </DialogContent>

          <DialogActions className="flex">
            <Button variant="text" className="w-full" onClick={onClose}>
              Cancel
            </Button>

            <Button form="form" type="submit" variant="contained" className="w-full">
              Save changes
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default CustomerDialog;
